/**
 * Label (DEC-117) — standalone display text. Fires NOTHING.
 *
 * The simplest item in the palette by design. No pointer handlers at all:
 * it claims no CC, takes no part in guard / performance-mode gesture handling,
 * and is invisible to the SD-14 dup-CC linter. In edit mode the wrapper
 * (EditableItem) owns every gesture, exactly as it does for any item; in
 * test/performance mode a label is inert, so taps fall through harmlessly.
 *
 * Multi-line text renders with `white-space: pre-line` per DEC-052, so a
 * Shift+Enter newline authored in the Inspector shows up as a line break.
 *
 * The optional background + border also cover the TouchOSC BOX case (thin
 * decorative frames / dividers), which is why BOX does not need its own
 * item kind.
 */

import type { LabelItem, LayoutColor } from '../layout/store.ts';
import { activePage } from '../layout/projectStore.ts';
import { isEditMode } from '../layout/store.ts';
import { TRAY_KINDS, trayTitleText } from './TrayTitle.tsx';

/** True when a button grid on this page shows a title with the same words (DEC-321, operator: "Hide them"): the
 *  label is then saying the same thing twice. Nothing is deleted: turn the grid's title off and the label returns.
 *  Same words = trimmed, case and inner spaces ignored (the operator's "F Drums" label, saved with a trailing line
 *  break, matches a grid called "F drums"). */
const norm = (s: string | undefined) => (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
export function labelRepeatsGridTitle(item: LabelItem): boolean {
  const t = norm(item.text);
  if (!t) return false;
  return (activePage()?.items ?? []).some((it) => (it.kind === 'buttongrid' ? norm(it.name) === t
    : TRAY_KINDS.has(it.kind) && norm(trayTitleText(it)) === t) && (it.titleAt ?? 'tl') !== 'off');
}

interface LabelProps {
  item: LabelItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Font size per NameSize preset. `auto` scales with the item's height so
 *  a resized heading keeps its proportions; the explicit locks are fixed
 *  pixel sizes matching the face-label vocabulary (DEC-053). */
function fontSizeFor(item: LabelItem): string {
  switch (item.textSize) {
    case 'S':  return '13px';
    case 'M':  return '18px';
    case 'L':  return '26px';
    case 'XL': return '38px';
    default:   return `clamp(11px, ${Math.round(item.h * 0.42)}px, 48px)`;
  }
}

export function Label(props: LabelProps) {
  const align = () => props.item.align ?? 'center';
  const valign = () => props.item.valign ?? 'middle';

  return (
    <div
      class="label-item"
      data-token={COLOR_TOKEN[props.item.color]}
      data-align={align()}
      data-valign={valign()}
      data-bg={props.item.background ? 'true' : 'false'}
      data-border={props.item.border ? 'true' : 'false'}
      data-repeats-title={labelRepeatsGridTitle(props.item) ? (isEditMode() ? 'edit' : 'hide') : undefined}
      title={labelRepeatsGridTitle(props.item) && isEditMode() ? 'Hidden on stage: the grid shows this as its title' : undefined}
      style={{ 'font-size': fontSizeFor(props.item) }}
    >
      <span class="label-item-text">{props.item.text}</span>
    </div>
  );
}
